import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { ReactNode } from 'react'
import React from 'react'

import { overrideOptional } from '@emico/overrides'
import { theme } from '@emico/styles'

type LabelArrowPosition = 'none' | 'left' | 'right'

interface LabelProps {
  arrowPosition?: LabelArrowPosition
  color?: string
  size?: number
  arrowSize?: number
  children?: ReactNode
  className?: string
}

const StyledArrow = styled.svg<LabelProps>`
  fill: ${(props) => props.color ?? theme.primaryColor};
  position: absolute;
  top: 0;
  bottom: 0;
  height: 100%;
  width: ${(props) => props.arrowSize ?? 10}px;

  ${(props) =>
    props.arrowPosition === 'left'
      ? css`
          transform: scaleX(-1);
          left: -${props.arrowSize ?? 10}px;
        `
      : css`
          right: -${props.arrowSize ?? 10}px;

          @media not all and (min-resolution: 0.001dpcm) {
            @supports (-webkit-appearance: none) {
              right: calc(-${props.arrowSize ?? 10}px - -0.2px);
            }
          }
        `}
`

const LabelWrapper = styled('div', {
  shouldForwardProp: (prop) =>
    !['color', 'arrowPosition', 'arrowSize'].includes(String(prop)),
})<LabelProps>`
  background: ${(props) => props.color ?? theme.primaryColor};
  color: ${theme.onPrimaryColor};
  display: inline-block;
  font-family: ${theme.fontFamilyPrimary};
  padding: ${theme.spacing.x1}px ${theme.spacing.x2}px;
  position: relative;

  ${(props) =>
    props.arrowPosition === 'left' &&
    css`
      margin-left: ${props.arrowSize ?? 10}px;
    `}

  ${(props) =>
    props.arrowPosition === 'right' &&
    css`
      margin-right: ${props.arrowSize ?? 10}px;
    `}
`

const Arrow = (props: LabelProps) => (
  <StyledArrow viewBox="0 0 10 10" preserveAspectRatio="none" {...props}>
    <path d="M 0 0 L 10 5 L 0 10 L 0 0" />
  </StyledArrow>
)

const Label = ({ children, arrowPosition, className, ...rest }: LabelProps) => (
  <LabelWrapper arrowPosition={arrowPosition} className={className} {...rest}>
    {arrowPosition !== 'none' && (
      <Arrow arrowPosition={arrowPosition} {...rest} />
    )}
    {children}
  </LabelWrapper>
)

export default overrideOptional(Label)
